// "New Product Approval" popup.
//   mode="create"       coach / medical incharge suggest a product. Max price
//                       and vendor details are shown greyed out - the purchase
//                       manager fills them.
//   mode="pm-review"    purchase manager: product details read-only. Accept or
//                       Reject; after Accept fills max price + vendor and
//                       forwards it to the admin.
//   mode="admin-review" admin: product details read-only. Purchase manager
//                       approved it -> max price + vendor read-only, final
//                       approve / reject. Otherwise (not reviewed yet, or
//                       rejected by the purchase manager) the admin fills max
//                       price + vendor to approve it directly.
//   mode="view"         read-only.
import React, { useEffect, useRef, useState } from 'react';
import { X, UploadCloud, Store, Paperclip, BadgeCheck, XCircle, Info } from 'lucide-react';
import {
    createProductApproval,
    approveProductApproval,
    rejectProductApproval,
    getProductApprovalAttachment,
    getVendorOptions,
} from '../../../api';
import showToast from '../../../utils/toast';
import { pmDecisionOf, isRejectedByPm } from './productApprovalReview';
import './AddPurchaseRequestModal.css';
import './NewProductApprovalModal.css';
import SearchableSelect from '../../common/SearchableSelect';

const CATEGORIES = ['ISF Shop', 'Medicines', 'Consumables', 'Repairs', 'Infra', 'Others'];

const PRIORITIES = [
    { value: 'high', label: 'High', color: '#dc2626' },
    { value: 'medium', label: 'Medium', color: '#d97706' },
    { value: 'low', label: 'Low', color: '#2563eb' },
];

const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_FILES = 5;
const DESCRIPTION_LIMIT = 500;
const PHONE_REGEX = /^(\+91[-\s]?)?[6789]\d{9}$/;

// Shows a validation error and tells run() not to close the modal.
const fail = (message) => {
    showToast(message, 'error');
    return false;
};

const toDateInput = (value) => {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
};

const emptyVendor = { mode: 'existing', vendorId: '', name: '', address: '', phone: '', contactPerson: '' };

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('en-IN') : '');

// Banner on review / view: what the purchase manager decided, and what the
// reviewer has to do next.
const reviewBannerFor = (approval, isAdminReview) => {
    if (!approval) return null;
    const pmDecision = pmDecisionOf(approval);
    const pmName = approval.pmReviewedBy?.name || 'Purchase Manager';
    const on = approval.pmReviewedAt ? ` on ${formatDate(approval.pmReviewedAt)}` : '';
    if (pmDecision === 'approved') {
        return {
            tone: 'approved',
            Icon: BadgeCheck,
            title: 'Approved by Purchase Manager',
            text: `${pmName}${on} approved this product and filled in the maximum price and vendor details.`,
        };
    }
    if (pmDecision === 'rejected') {
        return {
            tone: 'rejected',
            Icon: XCircle,
            title: 'Rejected by Purchase Manager',
            text: isAdminReview
                ? `${pmName}${on} rejected this product. You can still approve it - fill in the maximum price and vendor details below.`
                : approval.status === 'approved'
                ? `${pmName}${on} rejected this product; the admin approved it anyway and filled in the maximum price and vendor details.`
                : `${pmName}${on} rejected this product.`,
        };
    }
    if (isAdminReview) {
        return {
            tone: 'info',
            Icon: Info,
            title: 'Not reviewed by Purchase Manager yet',
            text: 'To approve it directly, fill in the maximum price and vendor details below.',
        };
    }
    if (approval.status === 'approved') {
        return {
            tone: 'info',
            Icon: Info,
            title: 'Approved directly by Admin',
            text: 'The admin filled in the maximum price and vendor details.',
        };
    }
    return null;
};

export default function NewProductApprovalModal({
    mode = 'create',
    approval = null,
    balagruhas = [],
    onClose,
    onDone = () => {},
    // pm-review only: open with Accept already clicked (Accept on the Purchase
    // Approvals details panel).
    startAccepted = false,
}) {
    const isCreate = mode === 'create';
    const isPmReview = mode === 'pm-review';
    const isAdminReview = mode === 'admin-review';
    const productLocked = !isCreate;
    // Purchase manager: Accept first, then max price + vendor unlock.
    const [pmAccepted, setPmAccepted] = useState(isPmReview && startAccepted);
    // Admin fills max price + vendor unless the purchase manager already did.
    const adminFillsPricing = isAdminReview && approval?.status !== 'pending_admin';
    const canFillPricing = isPmReview || adminFillsPricing;
    const pricingEditable = (isPmReview && pmAccepted) || adminFillsPricing;
    // A purchase manager rejection can only be overruled (approved), not rejected again.
    const canReject = (isPmReview && !pmAccepted) || (isAdminReview && !isRejectedByPm(approval));
    const reviewBanner = isCreate || isPmReview ? null : reviewBannerFor(approval, isAdminReview);

    const [balagruhaId, setBalagruhaId] = useState(
        approval?.balagruhaId?._id || approval?.balagruhaId || (balagruhas.length === 1 ? balagruhas[0]._id : '')
    );
    const [priority, setPriority] = useState(approval?.priority || 'medium');
    const [deadline, setDeadline] = useState(toDateInput(approval?.deadline));
    const [productName, setProductName] = useState(approval?.productName || '');
    const [category, setCategory] = useState(approval?.category || '');
    const [quantity, setQuantity] = useState(approval?.quantity ?? '');
    const [description, setDescription] = useState(approval?.description || '');
    const [files, setFiles] = useState([]);
    const [dragOver, setDragOver] = useState(false);

    const [maxPrice, setMaxPrice] = useState(approval?.maxPrice ?? '');
    const [vendor, setVendor] = useState(() => ({
        ...emptyVendor,
        ...(approval?.vendor || {}),
        mode: approval?.vendor?.mode || 'existing',
        vendorId: approval?.vendor?.vendorId?._id || approval?.vendor?.vendorId || '',
    }));
    const [vendors, setVendors] = useState([]);
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef(null);
    const priceInputRef = useRef(null);

    useEffect(() => {
        if (pmAccepted) priceInputRef.current?.focus();
    }, [pmAccepted]);

    useEffect(() => {
        if (!canFillPricing) return;
        let cancelled = false;
        (async () => {
            try {
                const response = await getVendorOptions();
                if (!cancelled) setVendors(response?.vendors || []);
            } catch (error) {
                if (!cancelled) showToast('Failed to load vendors', 'error');
            }
        })();
        return () => { cancelled = true; };
    }, [canFillPricing]);

    const selectedVendor = vendor.mode === 'existing'
        ? vendors.find((v) => v._id === vendor.vendorId) || (vendor.vendorId && !pricingEditable ? vendor : null)
        : null;

    // Reviewers may not be assigned to the requester's Balagruha, so a saved
    // approval always shows its own Balagruha.
    const balagruhaOptions = !isCreate && approval?.balagruhaId?._id
        ? [{ _id: approval.balagruhaId._id, name: approval.balagruhaId.name }]
        : balagruhas;

    const addFiles = (list) => {
        const incoming = Array.from(list || []);
        const accepted = [];
        incoming.forEach((file) => {
            if (!ALLOWED_TYPES.includes(file.type)) {
                showToast(`${file.name}: only JPG, PNG or PDF files are allowed`, 'error');
            } else if (file.size > MAX_FILE_SIZE) {
                showToast(`${file.name}: file is larger than 5 MB`, 'error');
            } else {
                accepted.push(file);
            }
        });
        setFiles((prev) => {
            const next = [...prev, ...accepted];
            if (next.length > MAX_FILES) showToast(`You can attach up to ${MAX_FILES} files`, 'error');
            return next.slice(0, MAX_FILES);
        });
    };

    const openAttachment = async (attachment) => {
        try {
            const blob = await getProductApprovalAttachment(approval._id, attachment._id);
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (error) {
            showToast('Could not open attachment', 'error');
        }
    };

    const handleCreate = async () => {
        const todayStr = new Date().toISOString().split('T')[0];
        const qty = Number(quantity);
        if (!balagruhaId) return fail('Please select a Balagruha');
        if (!deadline) return fail('Please select a deadline / due date');
        if (deadline < todayStr) return fail('Deadline cannot be in the past');
        if (!productName.trim()) return fail('Please enter the product name');
        if (!category) return fail('Please select a category');
        if (!Number.isInteger(qty) || qty < 1) return fail('Please enter a valid quantity (>= 1)');
        if (!description.trim()) return fail('Please enter a description');

        const data = new FormData();
        data.append('balagruhaId', balagruhaId);
        data.append('priority', priority);
        data.append('deadline', deadline);
        data.append('productName', productName.trim());
        data.append('category', category);
        data.append('quantity', String(qty));
        data.append('description', description.trim());
        files.forEach((file) => data.append('attachments', file));

        const response = await createProductApproval(data);
        if (!response?.success) throw new Error(response?.message);
        showToast('Product submitted for approval', 'success');
        return true;
    };

    // Approve with the max price + vendor typed in here (purchase manager, or
    // admin approving directly).
    const handleApproveWithPricing = async (successMessage) => {
        const price = Number(maxPrice);
        if (maxPrice === '' || !Number.isFinite(price) || price <= 0) {
            return fail('Please enter a valid maximum price');
        }
        let vendorPayload;
        if (vendor.mode === 'existing') {
            if (!vendor.vendorId) return fail('Please select a vendor');
            vendorPayload = { mode: 'existing', vendorId: vendor.vendorId, contactPerson: vendor.contactPerson };
        } else {
            if (!vendor.name.trim()) return fail('Please enter the vendor / shop name');
            if (!PHONE_REGEX.test(vendor.phone.trim())) return fail('Please enter a valid vendor phone number');
            if (!vendor.address.trim()) return fail('Please enter the vendor address');
            vendorPayload = {
                mode: 'new',
                name: vendor.name.trim(),
                phone: vendor.phone.trim(),
                address: vendor.address.trim(),
                contactPerson: vendor.contactPerson.trim(),
            };
        }
        const response = await approveProductApproval(approval._id, { maxPrice: price, vendor: vendorPayload, notes: notes.trim() });
        if (!response?.success) throw new Error(response?.message);
        showToast(successMessage, 'success');
        return true;
    };

    const run = async (action) => {
        try {
            setSubmitting(true);
            const result = await action();
            if (result === false) return;
            onDone();
        } catch (error) {
            showToast(error.response?.data?.message || error.message || 'Something went wrong', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (isCreate) run(handleCreate);
        else if (isPmReview) {
            if (pmAccepted) run(() => handleApproveWithPricing('Product accepted and sent to admin'));
        } else if (adminFillsPricing) run(() => handleApproveWithPricing('Product approved'));
        else if (isAdminReview) {
            run(async () => {
                const response = await approveProductApproval(approval._id, { notes: notes.trim() });
                if (!response?.success) throw new Error(response?.message);
                showToast('Product approved', 'success');
            });
        }
    };

    const handleReject = () => run(async () => {
        const response = await rejectProductApproval(approval._id, notes.trim());
        if (!response?.success) throw new Error(response?.message);
        showToast('Product rejected', 'success');
    });

    const title = isCreate ? 'New Product Approval' : isPmReview || isAdminReview ? 'Review Product Approval' : 'Product Approval';

    return (
        <div className="apr-overlay" onClick={onClose}>
            <div className="apr-modal npa-modal" onClick={(e) => e.stopPropagation()}>
                <div className="apr-header">
                    <h3 className="npa-title">
                        <span className="npa-title-icon"><Store size={16} color="#ffffff" /></span>
                        {title}
                        {approval?.approvalId && <span className="npa-id">{approval.approvalId}</span>}
                    </h3>
                    <button type="button" className="apr-close" onClick={onClose} aria-label="Close">
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="apr-body">
                        {!isCreate && approval?.requestedBy && (
                            <div className="npa-meta">
                                Requested by <strong>{approval.requestedBy.name || approval.requestedBy.email}</strong>
                                {approval.createdAt && <> on {new Date(approval.createdAt).toLocaleDateString('en-IN')}</>}
                            </div>
                        )}

                        {reviewBanner && (
                            <div className={`npa-review-banner npa-review-${reviewBanner.tone}`}>
                                <reviewBanner.Icon size={18} />
                                <div>
                                    <strong>{reviewBanner.title}</strong>
                                    <span>{reviewBanner.text}</span>
                                </div>
                            </div>
                        )}

                        <div className="npa-row">
                            <div className="apr-field">
                                <label>Balagruha Name <span className="apr-required">*</span></label>
                                <SearchableSelect searchPlaceholder="Search balagruha..." value={balagruhaId} onChange={(e) => setBalagruhaId(e.target.value)} disabled={productLocked}>
                                    <option value="">Select Balagruha</option>
                                    {balagruhaOptions.map((bg) => (
                                        <option key={bg._id} value={bg._id}>{bg.name}</option>
                                    ))}
                                </SearchableSelect>
                            </div>
                            <div className="apr-field">
                                <label>Priority <span className="apr-required">*</span></label>
                                <div className="apr-priority-row">
                                    {PRIORITIES.map((p) => (
                                        <button
                                            key={p.value}
                                            type="button"
                                            disabled={productLocked}
                                            className={`apr-priority-pill ${priority === p.value ? 'active' : ''}`}
                                            style={priority === p.value ? { background: p.color, borderColor: p.color } : { color: p.color, borderColor: p.color }}
                                            onClick={() => setPriority(p.value)}
                                        >
                                            {p.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="apr-field">
                            <label>Deadline / Due Date <span className="apr-required">*</span></label>
                            <input
                                type="date"
                                value={deadline}
                                min={isCreate ? new Date().toISOString().split('T')[0] : undefined}
                                onChange={(e) => setDeadline(e.target.value)}
                                disabled={productLocked}
                            />
                        </div>

                        <div className="apr-field">
                            <label>Product / Item Name <span className="apr-required">*</span></label>
                            <input
                                type="text"
                                value={productName}
                                maxLength={150}
                                placeholder="Enter product name"
                                onChange={(e) => setProductName(e.target.value)}
                                disabled={productLocked}
                            />
                        </div>

                        <div className="npa-row">
                            <div className="apr-field">
                                <label>Category <span className="apr-required">*</span></label>
                                <select value={category} onChange={(e) => setCategory(e.target.value)} disabled={productLocked}>
                                    <option value="">Select Category</option>
                                    {CATEGORIES.map((cat) => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="apr-field">
                                <label>Quantity <span className="apr-required">*</span></label>
                                <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={quantity}
                                    placeholder="Enter quantity"
                                    onChange={(e) => setQuantity(e.target.value)}
                                    disabled={productLocked}
                                />
                            </div>
                        </div>

                        <div className="apr-field">
                            <label>Description <span className="apr-required">*</span></label>
                            <textarea
                                rows={3}
                                value={description}
                                maxLength={DESCRIPTION_LIMIT}
                                placeholder="Enter detailed description of the product"
                                onChange={(e) => setDescription(e.target.value)}
                                disabled={productLocked}
                            />
                            {isCreate && <small className="npa-counter">{description.length}/{DESCRIPTION_LIMIT}</small>}
                        </div>

                        <div className="apr-field">
                            <label>Attachments</label>
                            {isCreate ? (
                                <>
                                    <div
                                        className={`npa-dropzone ${dragOver ? 'drag-over' : ''}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => fileInputRef.current?.click()}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                        onDragLeave={() => setDragOver(false)}
                                        onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                                    >
                                        <UploadCloud size={20} />
                                        <strong>Upload photo or PDF</strong>
                                        <span>Drag &amp; drop files here or click to upload</span>
                                        <small>(JPG, PNG, PDF | Max 5 MB each)</small>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            accept=".jpg,.jpeg,.png,.pdf"
                                            hidden
                                            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                                        />
                                    </div>
                                    {files.length > 0 && (
                                        <div className="apr-file-list">
                                            {files.map((file, index) => (
                                                <div className="apr-file-chip" key={`${file.name}-${index}`}>
                                                    <span>{file.name}</span>
                                                    <button type="button" onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))} aria-label="Remove file">
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (approval?.attachments || []).length > 0 ? (
                                <div className="apr-file-list">
                                    {approval.attachments.map((att) => (
                                        <button type="button" className="apr-file-chip npa-file-link" key={att._id} onClick={() => openAttachment(att)}>
                                            <Paperclip size={12} /> <span>{att.filename}</span>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <small className="apr-hint">No attachments</small>
                            )}
                        </div>

                        {/* Filled by the purchase manager (or the admin approving
                            directly) - greyed out for everyone else */}
                        <div className={`npa-pm-section ${pricingEditable ? '' : 'npa-locked'}`}>
                            {isCreate && (
                                <small className="npa-locked-hint">Maximum price and vendor details are filled by the Purchase Manager.</small>
                            )}
                            {isPmReview && !pmAccepted && (
                                <small className="npa-locked-hint">Accept the product to fill in the maximum price and vendor details.</small>
                            )}

                            <div className="apr-field">
                                <label>Maximum Price (₹) <span className="apr-required">*</span></label>
                                <div className="npa-price-input">
                                    <span>₹</span>
                                    <input
                                        ref={priceInputRef}
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={maxPrice}
                                        placeholder="Enter maximum price (per unit)"
                                        onChange={(e) => setMaxPrice(e.target.value)}
                                        disabled={!pricingEditable}
                                    />
                                </div>
                            </div>

                            <div className="apr-field">
                                <label>Vendor Details <span className="apr-required">*</span></label>
                                <div className="npa-radio-row">
                                    <label>
                                        <input
                                            type="radio"
                                            name="npa-vendor-mode"
                                            checked={vendor.mode === 'existing'}
                                            onChange={() => setVendor((v) => ({ ...v, mode: 'existing' }))}
                                            disabled={!pricingEditable}
                                        />
                                        Select from current vendors
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="npa-vendor-mode"
                                            checked={vendor.mode === 'new'}
                                            onChange={() => setVendor((v) => ({ ...v, mode: 'new' }))}
                                            disabled={!pricingEditable}
                                        />
                                        Create new vendor
                                    </label>
                                </div>
                            </div>

                            {vendor.mode === 'existing' ? (
                                <>
                                    <div className="apr-field">
                                        <label>Vendor / Shop Name <span className="apr-required">*</span></label>
                                        {pricingEditable ? (
                                            <SearchableSelect
                                                searchPlaceholder="Search vendor..."
                                                value={vendor.vendorId}
                                                onChange={(e) => setVendor((v) => ({ ...v, vendorId: e.target.value }))}
                                            >
                                                <option value="">Select vendor</option>
                                                {vendors.map((v) => (
                                                    <option key={v._id} value={v._id}>{v.name}</option>
                                                ))}
                                            </SearchableSelect>
                                        ) : (
                                            <input type="text" value={vendor.name || ''} placeholder="Select vendor" disabled />
                                        )}
                                    </div>
                                    <div className="npa-vendor-card">
                                        <div className="npa-vendor-card-title">Selected Vendor Details</div>
                                        <div><span>Shop Name</span>: {selectedVendor?.name || '-'}</div>
                                        <div><span>Address</span>: {selectedVendor?.address || '-'}</div>
                                        <div><span>Phone</span>: {selectedVendor?.phone || '-'}</div>
                                        <div className="npa-inline-field">
                                            <span>Contact Person</span>:
                                            {pricingEditable ? (
                                                <input
                                                    type="text"
                                                    value={vendor.contactPerson || ''}
                                                    placeholder="Optional"
                                                    onChange={(e) => setVendor((v) => ({ ...v, contactPerson: e.target.value }))}
                                                />
                                            ) : (
                                                <> {vendor.contactPerson || '-'}</>
                                            )}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="npa-row">
                                        <div className="apr-field">
                                            <label>Vendor / Shop Name <span className="apr-required">*</span></label>
                                            <input
                                                type="text"
                                                value={vendor.name}
                                                placeholder="Enter shop name"
                                                onChange={(e) => setVendor((v) => ({ ...v, name: e.target.value }))}
                                                disabled={!pricingEditable}
                                            />
                                        </div>
                                        <div className="apr-field">
                                            <label>Phone <span className="apr-required">*</span></label>
                                            <input
                                                type="text"
                                                value={vendor.phone}
                                                placeholder="10-digit mobile"
                                                onChange={(e) => setVendor((v) => ({ ...v, phone: e.target.value }))}
                                                disabled={!pricingEditable}
                                            />
                                        </div>
                                    </div>
                                    <div className="apr-field">
                                        <label>Address <span className="apr-required">*</span></label>
                                        <input
                                            type="text"
                                            value={vendor.address}
                                            placeholder="Enter address"
                                            onChange={(e) => setVendor((v) => ({ ...v, address: e.target.value }))}
                                            disabled={!pricingEditable}
                                        />
                                    </div>
                                    <div className="apr-field">
                                        <label>Contact Person</label>
                                        <input
                                            type="text"
                                            value={vendor.contactPerson}
                                            placeholder="Optional"
                                            onChange={(e) => setVendor((v) => ({ ...v, contactPerson: e.target.value }))}
                                            disabled={!pricingEditable}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        {(approval?.pmNotes || approval?.adminNotes) && (
                            <div className="npa-notes-readonly">
                                {approval.pmNotes && <div><strong>Purchase Manager:</strong> {approval.pmNotes}</div>}
                                {approval.adminNotes && <div><strong>Admin:</strong> {approval.adminNotes}</div>}
                            </div>
                        )}

                        {(isPmReview || isAdminReview) && (
                            <div className="apr-field">
                                <label>Remarks (Optional)</label>
                                <textarea
                                    rows={2}
                                    value={notes}
                                    maxLength={500}
                                    placeholder="Reason for rejection or a note for the next approver"
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            </div>
                        )}
                    </div>

                    <div className="apr-footer">
                        <button type="button" className="apr-btn-cancel" onClick={onClose} disabled={submitting}>
                            {mode === 'view' ? 'Close' : 'Cancel'}
                        </button>
                        {isPmReview && pmAccepted && (
                            <button type="button" className="apr-btn-cancel" onClick={() => setPmAccepted(false)} disabled={submitting}>
                                Back
                            </button>
                        )}
                        {canReject && (
                            <button type="button" className="npa-btn-reject" onClick={handleReject} disabled={submitting}>
                                Reject
                            </button>
                        )}
                        {isPmReview && !pmAccepted && (
                            <button type="button" className="npa-btn-accept" onClick={() => setPmAccepted(true)} disabled={submitting}>
                                Accept
                            </button>
                        )}
                        {mode !== 'view' && !(isPmReview && !pmAccepted) && (
                            <button type="submit" className="apr-btn-submit npa-btn-submit" disabled={submitting}>
                                {submitting
                                    ? 'Please wait...'
                                    : isCreate
                                    ? 'Submit for Approval'
                                    : isPmReview
                                    ? 'Forward to Admin'
                                    : 'Approve'}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}
